// Cart utility functions

// Generate unique cart item ID based on item + variations + modifiers
const generateCartItemId = (item) => {
    const baseId = item.id;
    const variationId = item.selectedVariation?.id || 'no-variation';
    const modifierIds = (item.selectedModifiers || [])
        .map(m => m.id)
        .sort()
        .join('-') || 'no-modifiers';

    return `${baseId}_${variationId}_${modifierIds}`;
};

// Check if two items have the same customization
const isSameCustomization = (item1, item2) => {
    // Different base items
    if (item1.id !== item2.id) return false;

    // Compare variations
    const var1 = item1.selectedVariation?.id || null;
    const var2 = item2.selectedVariation?.id || null;
    if (var1 !== var2) return false;

    // Compare modifiers
    const mods1 = (item1.selectedModifiers || []).map(m => m.id).sort().join(',');
    const mods2 = (item2.selectedModifiers || []).map(m => m.id).sort().join(',');
    if (mods1 !== mods2) return false;

    return true;
};

export const addItemToCart = (cart, item) => {
    // Find existing item with same customization
    const existingItem = cart.find((cartItem) => isSameCustomization(cartItem, item));

    if (existingItem) {
        // Same customization exists - increase quantity
        return cart.map((cartItem) =>
            isSameCustomization(cartItem, item)
                ? { ...cartItem, quantity: cartItem.quantity + (item.quantity || 1) }
                : cartItem
        );
    }

    // New customization - add as new cart item with unique ID
    const cartItemId = generateCartItemId(item);
    return [...cart, { ...item, cartItemId, quantity: item.quantity || 1 }];
};

export const removeItemFromCart = (cart, cartItemId) => {
    const existingItem = cart.find((cartItem) => (cartItem.cartItemId || cartItem.id) === cartItemId);
    if (existingItem && existingItem.quantity > 1) {
        return cart.map((cartItem) =>
            (cartItem.cartItemId || cartItem.id) === cartItemId
                ? { ...cartItem, quantity: cartItem.quantity - 1 }
                : cartItem
        );
    }
    return cart.filter((cartItem) => (cartItem.cartItemId || cartItem.id) !== cartItemId);
};

export const deleteItemFromCart = (cart, cartItemId) => {
    return cart.filter((cartItem) => (cartItem.cartItemId || cartItem.id) !== cartItemId);
};

export const incrementCartItemQuantity = (cart, cartItemId) => {
    return cart.map((cartItem) =>
        (cartItem.cartItemId || cartItem.id) === cartItemId
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
    );
};

export const calculateCartTotal = (cart) => {
    return cart.reduce((total, item) => {
        const itemPrice = item.finalPrice || item.price;
        return total + itemPrice * item.quantity;
    }, 0);
};

/**
 * Calculate comprehensive order totals including service charges and VAT
 * @param {Array} cart - Cart items
 * @param {string} orderType - 'dine-in' or 'takeaway'
 * @param {Object} companyRates - Company service charge and tax rates
 * @returns {Object} Breakdown of all charges and totals
 */
export const calculateOrderTotals = (cart, orderType = 'takeaway', companyRates = {}) => {
    const {
        serviceChargeRateDining = 0,
        serviceChargeRateTakeaway = 0,
        vatRate = 0
    } = companyRates;

    // Calculate subtotal
    const subtotal = calculateCartTotal(cart);

    // Determine service charge rate based on order type
    const serviceChargeRate = orderType === 'dine-in'
        ? serviceChargeRateDining
        : serviceChargeRateTakeaway;

    // Calculate service charge
    const serviceCharge = (subtotal * serviceChargeRate) / 100;

    // Calculate subtotal after service charge
    const subtotalWithServiceCharge = subtotal + serviceCharge;

    // Calculate VAT (on subtotal + service charge) - VAT applies to ALL orders
    const vatAmount = (subtotalWithServiceCharge * vatRate) / 100;

    // Calculate final total
    const grandTotal = subtotalWithServiceCharge + vatAmount;

    return {
        subtotal: subtotal,
        serviceChargeRate: serviceChargeRate,
        serviceCharge: serviceCharge,
        vatRate: vatRate,
        vatAmount: vatAmount,
        grandTotal: grandTotal,
        orderType: orderType
    };
};

export const getCartItemCount = (cart) => {
    return cart.reduce((count, item) => count + item.quantity, 0);
};
